import { useEffect, useRef } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

import { toLatLng } from './geo';

/**
 * Every helper here is declared at module scope on purpose.
 *
 * The zone picker used to define its own equivalents *inside* the component
 * body: React saw a new component type on every render, so the whole map was
 * unmounted and rebuilt at each keystroke in the surrounding form.
 */

/**
 * Leaflet caches its container size and only re-reads it when told. Any panel
 * that animates open, any tab that reveals the map, leaves a band of grey
 * tiles that never loads until the user pans. The observer catches every frame
 * of a CSS transition; `transitionend` catches the final one.
 */
export function AutoInvalidateSize() {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    let frame = null;
    const invalidate = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = null;
        map.invalidateSize({ animate: false });
      });
    };

    invalidate();
    const settle = window.setTimeout(invalidate, 200);

    let observer = null;
    if (typeof window.ResizeObserver === 'function') {
      observer = new window.ResizeObserver(invalidate);
      observer.observe(container);
    }
    container.addEventListener('transitionend', invalidate);
    window.addEventListener('resize', invalidate);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      if (observer) observer.disconnect();
      container.removeEventListener('transitionend', invalidate);
      window.removeEventListener('resize', invalidate);
    };
  }, [map]);

  return null;
}

/**
 * Centres on a target. The effect keys on the two numbers rather than on the
 * array so a parent that rebuilds `[lat, lng]` each render does not re-fly the
 * map out from under the operator.
 */
export function MapFocus({ target, zoom, minZoom = 12, animate = true }) {
  const map = useMap();
  const point = toLatLng(target);
  const lat = point?.[0];
  const lng = point?.[1];

  useEffect(() => {
    if (lat == null || lng == null) return;
    const nextZoom = Number.isFinite(zoom) ? zoom : Math.max(map.getZoom(), minZoom);
    if (animate) map.flyTo([lat, lng], nextZoom, { duration: 0.8 });
    else map.setView([lat, lng], nextZoom);
  }, [lat, lng, zoom, minZoom, animate, map]);

  return null;
}

/** Frames a set of points. Once by default: refitting on every GPS message
 *  would yank the view away from whatever the operator was looking at. */
export function MapFitBounds({ points, enabled = true, once = true, padding = [40, 40] }) {
  const map = useMap();
  const didFit = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (once && didFit.current) return;
    const valid = (points || []).map(toLatLng).filter(Boolean);
    if (valid.length === 0) return;
    map.fitBounds(L.latLngBounds(valid), { padding });
    didFit.current = true;
  }, [points, enabled, once, padding, map]);

  return null;
}

/** Reports map clicks as `[lat, lng]`. */
export function MapClickHandler({ onClick }) {
  useMapEvents({
    click(event) {
      if (onClick) onClick([event.latlng.lat, event.latlng.lng]);
    },
  });
  return null;
}
